import { FormApi } from 'final-form';
import React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { AdFilterFields, AdFilterFormSubmitValues, DistanceFilterValues } from '@Components/forms/Filter/AdFilters';
import { AdSortNames } from '@Components/forms/inputs/selects/AdvertsSortSelect';
import { ModalType } from '@Components/Modal';
import { RouteConfig } from '@Config/routes';
import { GetMarketplaceAdsQueryVariables, useGetCategoriesLazyQuery } from '@Graphql/graphqlTypes.generated';
import { defaultFiltersValues } from '@Routes/company/CompanyPage';
import { getCurrentModal } from '@Store/app/app.selectors';
import { buildFilteredAdsUrl } from '@Utils/router';
import { isDefined } from '@Utils/tools';

import { clearEmptyValues, desc, parseJSON } from '../convertions';
import { URLSearchParamsHolder, urlDefaultParametersPicker, ExactKey, SearchParamsHolder } from '../types';

import useURLSearchTracker from './useURLSearchTracker';

export const useAdvertFilterData = () => {
  const navigate = useNavigate();

  const [getCategoryData] = useGetCategoriesLazyQuery({
    fetchPolicy: 'cache-and-network',
    partialRefetch: true,
  });

  const [urlSearchParams] = useURLSearchTracker<URLSearchParamsHolder>(
    {
      filter: undefined,
      sort: undefined,
    },
    urlDefaultParametersPicker,
  );

  const filterData = React.useMemo(() => parseJSON(urlSearchParams.filter, {}), [urlSearchParams.filter]);
  const sortData = React.useMemo(() => parseJSON(urlSearchParams.sort, {}), [urlSearchParams.sort]);
  const { geoSearch } = filterData;
  const distanceValueIsMinAllowed = filterData?.distance === DistanceFilterValues.min;

  const distanceFieldValue =
    filterData?.distance || distanceValueIsMinAllowed ? filterData?.distance : DistanceFilterValues.startValue;

  const geoSearchWithDistance = { ...geoSearch, distance: distanceFieldValue };
  const searchData = filterData.geoSearch ? { ...filterData, geoSearch: geoSearchWithDistance } : filterData;

  const isModalActive = useSelector(getCurrentModal);

  const querySortKey: ExactKey<GetMarketplaceAdsQueryVariables, 'orderby'> = 'orderby';
  const defaultSortValue = desc(AdSortNames.createdAt);

  const defaultQuerySortValues = {
    [querySortKey]: [defaultSortValue],
  };

  const filtersInitialValues: AdFilterFields = React.useMemo(
    () => ({
      ...defaultFiltersValues,
      ...filterData,
    }),
    [filterData],
  );

  const handleChange = <P extends Partial<SearchParamsHolder>>(change: P): Promise<{}> => {
    const allSearchParams: SearchParamsHolder = {
      filter: filterData,
      sort: sortData,
      ...change,
    };

    navigate(buildFilteredAdsUrl(allSearchParams), { replace: true });
    return Promise.resolve({});
  };

  const onFiltersChange = (values: AdFilterFields): Promise<{}> => {
    // TODO help
    // Problem No.1
    // This if statement is a hack. Needs more time to proper fix
    // After returning from modal url is changed (as should)
    // form initial values are changed then this func is invoked
    // and searchbar data is overwritten with old values
    // Problem No.2
    // This function is invoked every time slider value changes
    // causing re-rendering and api calls
    // maybe for slider call this function on realease rather than on val change
    values.categories && getCategoryData({ variables: { id: [values.categories[0]] } });
    if (isModalActive !== ModalType.filtersModal) {
      return handleChange({ filter: clearEmptyValues(values) });
    }
    return Promise.resolve({});
  };

  const onFiltersClear = (form: FormApi<AdFilterFormSubmitValues>) => {
    // we can only reset with setTimeout:
    // https://github.com/final-form/final-form/pull/229/
    setTimeout(form.reset);

    return handleChange({
      filter: undefined,
      sort: undefined,
    });
  };

  const onSortChange = (value: string): Promise<{}> => {
    return handleChange({ sort: clearEmptyValues({ [querySortKey]: value }) });
  };

  type MarketPlaceFiltersQueryUrlPayload = Partial<Omit<SearchParamsHolder, 'priceGte' | 'priceLte'>> & {
    priceRange?: {
      from?: string;
      to?: string;
    };
  };

  const buildMarketPlaceFiltersQueryUrl = (change: MarketPlaceFiltersQueryUrlPayload) => {
    const { priceRange, ...rest } = change;

    const priceRangeInput = {
      priceLte: isDefined(priceRange) && 'to' in priceRange ? priceRange.to : undefined,
      priceGte: isDefined(priceRange) && 'from' in priceRange ? priceRange.from : undefined,
    };

    const allSearchParams = {
      filter: {
        ...filterData,
        ...rest,
        ...priceRangeInput,
      },
    };

    const url = `${RouteConfig.Marketplace.buildLink()}${buildFilteredAdsUrl(allSearchParams)}`;
    return url;
  };

  return {
    filterData,
    sortData,
    geoSearchWithDistance,
    searchData,
    isModalActive,
    filtersInitialValues,
    defaultQuerySortValues,
    onFiltersClear,
    onFiltersChange,
    onSortChange,
    handleChange,
    buildMarketPlaceFiltersQueryUrl,
  };
};
